import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link, Outlet, useLoaderData, useNavigate, type LoaderFunctionArgs } from 'react-router-dom';
import { Button, EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import type {
  AdminMemberListItem,
  GetAdminOrganizationResponse,
  ListOrganizationRecordsResponse,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading, ColumnLabel } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { MailIcon, MembersIcon, OrganizationsIcon, TextIcon } from '@/components/layout/icons';
import { ResourceHeader } from '@/components/resource-header';
import { ARIA_SORT, SortableHeader, useTableSort, type SortValue } from '@/components/sortable-table';
import { OrganizationRecords } from '@/pages/organizations/organization-records';

type OrganizationDetailData = {
  readonly organization: GetAdminOrganizationResponse;
  readonly members: ReadonlyArray<AdminMemberListItem>;
  // Null when the Standort's records could not be loaded (IMM-1104).
  readonly records: ListOrganizationRecordsResponse | null;
};

export async function organizationDetailLoader({ params }: LoaderFunctionArgs): Promise<OrganizationDetailData> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing organization id', { status: 404 });
  }

  const [orgResult, membersResult, recordsResult] = await Promise.all([
    apiClient.adminOrganizations.get({ pathParams: { id } }),
    // Members are supplementary — the `.catch` keeps a rejection (network/fetch
    // failure) out of the `Promise.all` so it degrades to an empty panel rather
    // than dropping the whole page into the error boundary.
    apiClient.adminOrganizations.listMembers({ pathParams: { id } }).catch(() => null),
    // Same for the records the operator may move; a failure is said, not shown as none.
    apiClient.adminDeals.listOrganizationRecords({ pathParams: { id } }).catch(() => null),
  ]);

  if (!orgResult.ok) {
    throw new Response('Failed to load organization', { status: orgResult.status });
  }

  return {
    organization: orgResult.body,
    members: membersResult?.ok ? membersResult.body.data : [],
    records: recordsResult?.ok ? recordsResult.body : null,
  };
}

function formatCoordinates(latitude: number | null, longitude: number | null): string | null {
  if (latitude === null || longitude === null) return null;

  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
}

function formatAddressLine(address: GetAdminOrganizationResponse['address']): string {
  const street = [address.street, address.houseNumber]
    .filter((p) => p !== '')
    .join(' ')
    .trim();
  const city = [address.postalCode, address.city]
    .filter((p) => p !== '')
    .join(' ')
    .trim();

  return [street, city].filter((p) => p !== '').join(', ');
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

type MemberField = 'name' | 'email';

// A row with its full name resolved, so the sort key stays module-level and stable.
type MemberRow = { readonly member: AdminMemberListItem; readonly fullName: string | null };

function memberSortKey(row: MemberRow, field: MemberField): SortValue {
  switch (field) {
    case 'name':
      return row.fullName;
    case 'email':
      return row.member.email;
  }
}

export function OrganizationDetailPage() {
  const t = useTranslations('pages.admin.organizations.detail');
  const tList = useTranslations('pages.admin.organizations');
  const tTable = useTranslations('pages.admin.shell.table');
  const intl = useIntl();
  const navigate = useNavigate();
  const { organization, members, records } = useLoaderData() as OrganizationDetailData;
  const memberRows = useMemo(
    () =>
      members.map((member): MemberRow => {
        const fullName = `${member.firstName} ${member.lastName}`.trim();

        return { member, fullName: fullName === '' ? null : fullName };
      }),
    [members],
  );
  const { sorted, sort, toggleSort } = useTableSort(memberRows, memberSortKey);
  const coordinates = formatCoordinates(organization.address.latitude, organization.address.longitude);
  const addressLine = formatAddressLine(organization.address);

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={OrganizationsIcon}
            crumbs={[{ label: tList('title'), to: '/workspaces/organizations' }, { label: organization.name }]}
          />
        }
        trailing={
          <Button size="sm" asChild>
            <Link to={`/workspaces/organizations/${organization.id}/edit`}>{t('edit-cta')}</Link>
          </Button>
        }
      />
      <ResourceHeader
        icon={OrganizationsIcon}
        title={organization.name}
        meta={addressLine === '' ? tList(`types.${organization.type}`) : addressLine}
      />
      <div className="space-y-8 px-6 pb-8">
        <section className="space-y-2">
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <dt className="text-text-tertiary">{t('id-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{organization.id}</dd>
            <dt className="text-text-tertiary">{t('name-label')}</dt>
            <dd className="text-text-primary">{organization.name}</dd>
            <dt className="text-text-tertiary">{t('type-label')}</dt>
            <dd className="text-text-primary">{tList(`types.${organization.type}`)}</dd>
            <dt className="text-text-tertiary">{t('workspace-label')}</dt>
            <dd>
              <Link
                to={`/workspaces/${organization.workspaceId}`}
                className="text-interactive underline-offset-4 hover:underline"
              >
                {organization.workspaceName}
              </Link>
            </dd>
            <dt className="text-text-tertiary">{t('created-at-label')}</dt>
            <dd className="text-text-primary">{intl.formatDate(organization.createdAt, { dateStyle: 'long' })}</dd>
          </dl>
        </section>

        <section className="space-y-2">
          <SectionHeading>{t('address-heading')}</SectionHeading>
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <dt className="text-text-tertiary">{t('street-label')}</dt>
            <dd className="text-text-primary">{organization.address.street}</dd>
            <dt className="text-text-tertiary">{t('house-number-label')}</dt>
            <dd className="text-text-primary">{organization.address.houseNumber}</dd>
            <dt className="text-text-tertiary">{t('postal-code-label')}</dt>
            <dd className="text-text-primary">{organization.address.postalCode}</dd>
            <dt className="text-text-tertiary">{t('city-label')}</dt>
            <dd className="text-text-primary">{organization.address.city}</dd>
            <dt className="text-text-tertiary">{t('country-label')}</dt>
            <dd className="text-text-primary">{organization.address.country}</dd>
            {coordinates !== null && (
              <>
                <dt className="text-text-tertiary">{t('coordinates-label')}</dt>
                <dd className="font-mono text-sm text-text-primary">{coordinates}</dd>
              </>
            )}
          </dl>
        </section>

        <section className="space-y-3">
          <SectionHeading>{t('members-heading')}</SectionHeading>
          {members.length === 0 ? (
            <EmptyState
              icon={<MembersIcon className="h-10 w-10 text-neutral-300" aria-hidden />}
              title={t('members-empty')}
            />
          ) : (
            <Table flush>
              <TableHeader>
                <TableRow>
                  {(
                    [
                      { field: 'name', icon: TextIcon, column: t('members-columns.name') },
                      { field: 'email', icon: MailIcon, column: t('members-columns.email') },
                    ] as const
                  ).map((head) => (
                    <TableHead
                      key={head.field}
                      {...(sort !== null && sort.field === head.field
                        ? { 'aria-sort': ARIA_SORT[sort.direction] }
                        : {})}
                    >
                      <SortableHeader
                        field={head.field}
                        label={<ColumnLabel icon={head.icon}>{head.column}</ColumnLabel>}
                        ariaLabel={tTable('sort-label', { column: head.column })}
                        sort={sort}
                        onSort={toggleSort}
                      />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map(({ member, fullName }) => (
                  <TableRow key={member.id} interactive onClick={() => navigate(`/workspaces/members/${member.id}`)}>
                    <TableCell>{fullName ?? '—'}</TableCell>
                    <TableCell>{member.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        <section className="space-y-3">
          <SectionHeading>{tList('records.heading')}</SectionHeading>
          <OrganizationRecords key={organization.id} organizationId={organization.id} records={records} />
        </section>
      </div>
      <Outlet />
    </>
  );
}
